import React, { useRef, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

import * as QS from './question-shared';

export interface NumberWithSelectionQuestionProps extends QS.QuestionProps {
    showValidationMessage: boolean;
    validationLabel?: string;
}

export const NumberWithSelectQuestion: React.FC<NumberWithSelectionQuestionProps> = ({
    selector,
    styleName,
    children,
    type,
    value,
    placeholder,
    values,
    showValidationMessage,
    onChange,
    validationLabel,
    disabled,
}) => {
    const inputRef = useRef<HTMLInputElement>();
    const selectRef = useRef<HTMLSelectElement>();

    const computedStyle = {
        ...QS.styles.default,
        ...(styleName ? QS.styles[styleName] : {}),
    };

    const handleChange = useMemo(() => onChange as QS.onChangeType, [onChange]);
    return (
        <fieldset
            id={`text-with-select-${selector}`}
            data-testid={`text-with-select-${selector}`}
            style={computedStyle.fieldset}
            className={styleName && QS.classNames[styleName].fieldset}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <legend
                    id={`text-with-select-label-${selector}`}
                    style={{
                        ...computedStyle.legend,
                        alignContent: 'flex-start',
                        flex: 3,
                    }}
                    className={styleName && QS.classNames[styleName].legend}
                >
                    {children}
                </legend>
                <div
                    ref={inputRef}
                    style={{
                        alignContent: 'flex-end',
                        flex: 1,
                        marginRight: '10px',
                    }}
                >
                    {(value as QS.TextWithSelectValue)?.select === 'Acres' ||
                    (value as QS.TextWithSelectValue)?.select === undefined ? (
                        <NumericFormat
                            id={`text-with-select-textbox-${selector}`}
                            data-testid={`text-with-select-textbox-${selector}`}
                            aria-labelledby={`text-with-select-label-${selector}`}
                            value={(value as QS.TextWithSelectValue)?.text || ''}
                            placeholder={placeholder}
                            thousandSeparator={true}
                            decimalScale={2}
                            onValueChange={(values) => {
                                const { value } = values;
                                handleChange &&
                                    handleChange({
                                        text: value,
                                        select: selectRef.current.value,
                                    });
                            }}
                            style={{
                                ...computedStyle.input,
                                maxWidth: '100%',
                                textAlign: 'right',
                                paddingRight: '8px',
                            }}
                            className={styleName && QS.classNames[styleName].input}
                            disabled={disabled}
                        />
                    ) : (
                        <NumericFormat
                            id={`text-with-select-textbox-${selector}`}
                            data-testid={`text-with-select-textbox-${selector}`}
                            aria-labelledby={`text-with-select-label-${selector}`}
                            value={(value as QS.TextWithSelectValue)?.text || ''}
                            placeholder={placeholder}
                            thousandSeparator={true}
                            decimalScale={0}
                            onValueChange={(values) => {
                                const { value } = values;
                                handleChange &&
                                    handleChange({
                                        text: value,
                                        select: selectRef.current.value,
                                    });
                            }}
                            style={{
                                ...computedStyle.input,
                                maxWidth: '100%',
                                textAlign: 'right',
                                paddingRight: '8px',
                            }}
                            className={styleName && QS.classNames[styleName].input}
                            disabled={disabled}
                        />
                    )}
                </div>
                <div style={{ alignContent: 'flex-end', flex: 1 }}>
                    <select
                        ref={selectRef}
                        id={`text-with-select-dropdown-${selector}`}
                        data-testid={`text-with-select-dropdown-${selector}`}
                        value={(value as QS.TextWithSelectValue)?.select || ''}
                        onChange={(e) =>
                            handleChange &&
                            handleChange({
                                text: (value as QS.TextWithSelectValue)?.text || '',
                                select: e.target.value,
                            })
                        }
                        aria-labelledby={`text-with-select-label-${selector}`}
                        disabled={disabled}
                    >
                        {values?.map((v, i) => (
                            <option
                                key={i}
                                value={v.value}
                            >
                                {v.display}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {showValidationMessage && QS.validationMessage(type, validationLabel)}
        </fieldset>
    );
};
